import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { api } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { itemList, breadcrumbList } from "../lib/schema";
import WaterfallCard from "../components/WaterfallCard";
import { Search, ChevronDown } from "lucide-react";

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export default function Listing() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [categories, setCategories] = useState({ countries: [], states: [] });
  const q = params.get("q") || "";
  const country = params.get("country") || "";
  const state = params.get("state") || "";
  const PAGE_SIZE = 20;

  useSEO({
    title: q ? `“${q}” waterfalls | WaterfallFinder` : "Explore all waterfalls | WaterfallFinder",
    description:
      "Browse 100+ waterfalls worldwide on the map. Filter by country and state.",
    jsonLd: items.length > 0
      ? [
          itemList(q ? `Waterfalls matching “${q}”` : "All waterfalls", items),
          breadcrumbList([
            { name: "Home", path: "/" },
            { name: "Waterfalls", path: "/waterfalls" },
          ]),
        ]
      : undefined,
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [listResp, mapResp] = await Promise.all([
        api.get("/waterfalls", {
          params: { q: q || undefined, country: country || undefined, state: state || undefined, limit: PAGE_SIZE, skip: 0 },
        }),
        api.get("/waterfalls-map", {
          params: { q: q || undefined, country: country || undefined, state: state || undefined },
        }),
      ]);
      setItems(listResp.data.items || []);
      setTotal(listResp.data.total || 0);
      setMapMarkers(mapResp.data.items || []);
      setLoading(false);
    })();
  }, [q, country, state]);

  const loadMore = async () => {
    setLoadingMore(true);
    const r = await api.get("/waterfalls", {
      params: { q: q || undefined, country: country || undefined, state: state || undefined, limit: PAGE_SIZE, skip: items.length },
    });
    setItems((prev) => [...prev, ...(r.data.items || [])]);
    setLoadingMore(false);
  };

  useEffect(() => {
    (async () => {
      const r = await api.get("/categories");
      setCategories(r.data);
    })();
  }, []);

  const updateParam = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val);
    else p.delete(key);
    setParams(p);
  };

  // Map center — derived from all map markers (not just paginated list).
  const mapCenter = useMemo(() => {
    const pts = mapMarkers.length > 0 ? mapMarkers : items;
    if (pts.length === 0) return [20, 0];
    const avgLat = pts.reduce((s, w) => s + (w.lat || 0), 0) / pts.length;
    const avgLng = pts.reduce((s, w) => s + (w.lng || 0), 0) / pts.length;
    return [avgLat, avgLng];
  }, [items, mapMarkers]);

  return (
    <main className="pt-16">
      {/* Filters bar */}
      <div className="border-b border-stone-200 bg-white sticky top-16 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="filter-bar">
          <Pill value={country} placeholder="Country" onChange={(v) => updateParam("country", v)} options={[{value:"", label:"All countries"}, ...categories.countries.map(c=>({value:c.slug,label:c.name}))]} testid="filter-country" />
          <Pill value={state} placeholder="State" onChange={(v) => updateParam("state", v)} options={[{value:"", label:"All states"}, ...categories.states.map(s=>({value:s.slug,label:s.name}))]} testid="filter-state" />
          <div className="flex-1" />
          <form onSubmit={(e)=>{e.preventDefault();updateParam("q",e.target.q.value);}} className="search-pill !py-2 !px-4 min-w-[260px]" data-testid="listing-search-form">
            <Search className="w-4 h-4 text-stone-500 mr-2" />
            <input name="q" defaultValue={q} placeholder="Search" data-testid="listing-search-input" />
          </form>
        </div>
      </div>

      {/* Split view: list (left) + map (right) */}
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-0">
        <div className="px-4 sm:px-6 lg:px-10 py-6" data-testid="listing-results">
          <h1 className="text-2xl font-extrabold text-stone-900 mb-1">Explore waterfalls</h1>
          <p className="text-sm text-stone-500 mb-6">{total} waterfalls{q?` matching "${q}"`:""}</p>
          {loading ? (
            <p className="text-stone-500">Loading…</p>
          ) : items.length === 0 ? (
            <div className="py-20 text-center" data-testid="listing-empty">
              <p className="text-stone-700">No waterfalls match. Try clearing filters.</p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-10" data-testid="listing-grid">
                {items.map((w) => (
                  <WaterfallCard key={w.id} wf={w} />
                ))}
              </div>
              {items.length < total && (
                <div className="mt-10 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    data-testid="listing-load-more"
                    className="btn-at btn-at-black min-w-[240px]"
                  >
                    {loadingMore ? "Loading…" : `Load more (${total - items.length} left)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <div className="hidden lg:block listing-map" data-testid="listing-map">
          <MapContainer center={mapCenter} zoom={mapMarkers.length > 1 ? 2 : 4} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {mapMarkers.filter((w) => w.lat && w.lng).map((w) => (
              <Marker key={w.id} position={[w.lat, w.lng]}>
                <Popup>
                  <a href={`/waterfalls/${w.slug}`} className="font-semibold">{w.name}</a>
                  <br />
                  <span className="text-xs text-stone-500">{w.city || w.country}</span>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    </main>
  );
}

function Pill({ value, placeholder, onChange, options, testid }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="appearance-none rounded-full border border-stone-300 hover:border-stone-900 pl-4 pr-9 py-2 text-sm font-semibold text-stone-900 bg-white outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown className="w-4 h-4 text-stone-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}
